import { Inject, inject, Injectable } from '@angular/core';
import { getApp } from '@angular/fire/app';
import { collection, collectionData, doc, docData, DocumentData, getFirestore, limit, orderBy, query, where } from '@angular/fire/firestore';
import { deleteObject, getDownloadURL, getMetadata, getStorage, ref, StorageReference, updateMetadata, uploadBytes, UploadMetadata } from '@angular/fire/storage';
import { ActivatedRouteSnapshot, Resolve, ResolveFn } from '@angular/router';
import { COMMONS_ENV, FirebaseObservableService, FirebaseObservableServiceFactory, HttpService, ObservableQueryService } from '@damofire/data-angular-commons';
import {
  ID_FIELD_OPTIONS,
  PRODUCTION_CUTTING_ITEMS_DESCRIPTIONS_COLLECTION,
  PRODUCTION_DRAWING_ITEMS_COLLECTION,
  PRODUCTION_DRAWING_ITEMS_DESCRIPTIONS_COLLECTION,
  PRODUCTION_DRAWING_STATUS_COLLECTION,
  PRODUCTION_DRAWINGS_COLLECTION,
  PRODUCTION_SUPPLIERS_COLLECTION,
  Query,
  SearchResult,
} from '@damofire/data-commons';
import { AuthDtoSignerService, AuthenticationService } from '@damofire/data-security';
import { QueryConstraint } from '@firebase/firestore';
import { firstValueFrom, Observable, Subscriber } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import {
  DrawingGenerationResponse,
  GenerationParameters,
  ProductionDrawing,
  ProductionDrawingFileType,
  ProductionDrawingParams,
  ProductionDrawingProductType,
} from './production-drawing.dto';
import { ProductionItem, ProductionMaterial, Supplier } from './production-item.dto';
import { RegroupedItem, SalesOrderModel } from './sale-order.dto';
import { ProductionSalesOrdersService } from './sales-orders.service';
import { UnitGenerationParameters } from './unit-drawing.dto';

export const DRAWING_NOT_FOUND = 'DRAWING_NOT_FOUND';
export const sortProductionMaterialByIndex = (a: ProductionMaterial, b: ProductionMaterial) => (a.index || 0) - (b.index || 0);
export const unicodeBtoa = (str: string): string => btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))));
export const unicodeAtob = (str: string): string =>
  decodeURIComponent(
    atob(str)
      .split('')
      .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
      .join(''),
  );

export function getProductType(filename: string): ProductionDrawingProductType {
  if (filename.startsWith('HO')) {
    return ProductionDrawingProductType.HOOK_OVER;
  } else if (filename.startsWith('AB')) {
    return ProductionDrawingProductType.BRACE;
  } else if (filename.startsWith('DFX')) {
    return ProductionDrawingProductType.PLATE;
  } else if (filename.startsWith('HARDWARE')) {
    return ProductionDrawingProductType.HARDWARE;
  } else if (filename.startsWith('FE')) {
    return ProductionDrawingProductType.FENCE;
  } else if (filename.startsWith('DEG')) {
    return ProductionDrawingProductType.END_GUARD;
  } else {
    return ProductionDrawingProductType.UNIT;
  }
}

export async function fetchFileMetadata(drawingRef: StorageReference): Promise<any> {
  let drawingParameters;
  try {
    const encodedMeta: UploadMetadata = await getMetadata(drawingRef);
    drawingParameters = JSON.parse(unicodeAtob(encodedMeta.customMetadata['drawingParameters']));
  } catch (e) {
    drawingParameters = null;
  }
  return drawingParameters;
}

/**
 * The file metadata may be missing (ex: the drawing was deleted from the bucket before being replaced).
 * The drawing parameters are required to regenerate the drawing (SVG) and to keep the drawing document
 * complete, so we take them from the drawing document when they are not available in the file metadata.
 */
export function fillDrawingParameters(params: ProductionDrawingParams | null | undefined, drawing?: ProductionDrawing): ProductionDrawingParams {
  const merged: ProductionDrawingParams = { ...params };
  if (!merged.drawingParameters && drawing?.params?.drawingParameters) {
    merged.drawingParameters = drawing.params.drawingParameters;
  }
  return merged;
}

export interface ProductionDrawingFilename {
  filename: string;
  productType: ProductionDrawingProductType;
  job: string;
  model: string;
}

export const parseProductionDrawingFileName = (filename: string): ProductionDrawingFilename => {
  const generalMatch = /^([A-Z_.\d]+)-(\d{8})([A-Z\d]+)/i;
  const drawingFilename = { filename } as ProductionDrawingFilename;

  let match;
  if (filename.startsWith('FE') || filename.startsWith('DEG')) {
    const fenceRegex = /(FE|DEG)-([\dX]+)-(\d{8})([A-Z\d]+)/i;
    match = fenceRegex.exec(filename);
    if (match) {
      drawingFilename.productType = match[1] === 'FE' ? ProductionDrawingProductType.FENCE : ProductionDrawingProductType.END_GUARD;
      drawingFilename.job = match[3] ? `${+match[3]}` : null;
      drawingFilename.model = match[4] ?? null;
    }
  } else {
    match = generalMatch.exec(filename);
    if (match) {
      drawingFilename.productType = getProductType(filename);
      drawingFilename.job = match[2] ? `${+match[2]}` : null;
      drawingFilename.model = match[3] ?? null;
    }
  }

  return drawingFilename;
};

@Injectable()
export abstract class ProductionDrawingsService implements Resolve<string> {
  abstract drawing(filename: string): Promise<ProductionDrawing | null>;
  abstract search(q: Query): Observable<SearchResult<ProductionDrawing>>;
  abstract resolve(route: ActivatedRouteSnapshot): Promise<string>;
  abstract download(path: string, fileType: ProductionDrawingFileType): Promise<string | null>;
  abstract get(path: string, fileType: ProductionDrawingFileType): Promise<string | null>;
  abstract delete(path: string): Promise<void> | null;
  abstract generateDrawing(drawingData: UnitGenerationParameters): Promise<Observable<DocumentData>>;
  abstract generateModelOnlyDrawing(salesOrderModel: SalesOrderModel): Promise<string>;
  abstract getDrawingStatusByName(name: string): Observable<DrawingGenerationResponse>;
  abstract uploadNew(file: File, drawing: GenerationParameters): Promise<void>;
  abstract uploadReplace(file: File, drawing: ProductionDrawing): Promise<void>;
  abstract updateDrawingModelItems(drawing: GenerationParameters): Promise<void>;
  abstract drawingMaterialsByJob(job: string): Observable<ProductionMaterial[]>;
  abstract drawingMaterialsByFilename(filename: string): Observable<ProductionMaterial[]>;
  abstract addDrawingItem(item: ProductionMaterial): Promise<void>;
  abstract updateDrawingItem(color: string, item: ProductionMaterial, qty: number, productionItem: ProductionItem): Promise<void>;
  abstract updateDrawingItems(filename: string, unitType: string, items: ProductionMaterial[]): Observable<DrawingGenerationResponse>;
  abstract searchSuppliers(queryObj: Query): Observable<Partial<Supplier>[]>;
  abstract listDrawingItemDescriptions(searchPhrase?: string): Promise<string[]>;
  abstract listCuttingItemDescriptions(group: string, searchPhrase?: string): Promise<string[]>;
  abstract rawSearch(q: Query): Promise<SearchResult<ProductionDrawing>>;
  abstract drawingCount(q: Query): Promise<number>;
  abstract deleteDrawingData(drawingId: string): Promise<void>;
  abstract updateDrawingData(drawingId: string, salesOrderModel: SalesOrderModel): Promise<void>;
  abstract getByJob(job: string): Observable<ProductionDrawing[]>;
  abstract saveDrawingProducts(params: GenerationParameters): Promise<void>;
}

@Injectable()
export class FirebaseProductionDrawingsService implements ProductionDrawingsService {
  private readonly service: FirebaseObservableService<ProductionDrawing>;

  constructor(
    readonly firebaseObservableServiceFactory: FirebaseObservableServiceFactory,
    private readonly salesOrderService: ProductionSalesOrdersService,
    private readonly authService: AuthenticationService,
    private readonly http: HttpService,
    private readonly dtoSignerService: AuthDtoSignerService,
    @Inject(COMMONS_ENV) private readonly environment: any,
    private readonly queryService: ObservableQueryService,
  ) {
    this.service = firebaseObservableServiceFactory.createService(PRODUCTION_DRAWINGS_COLLECTION);
  }

  async updateDrawingData(drawingId: string, salesOrderModel: SalesOrderModel): Promise<void> {
    await this.http.patch(`df-drawings/${drawingId}`, { salesOrderModel });
  }

  async saveDrawingProducts(params: GenerationParameters): Promise<void> {
    const { jobNumber, model, modelItems, regroupedItems } = params;
    console.log(`Saving drawing products for ${jobNumber} / ${model}`);

    const salesOrderModel: SalesOrderModel = await firstValueFrom(this.salesOrderService.getModel(jobNumber, model));
    salesOrderModel.items = modelItems;
    if (regroupedItems) {
      regroupedItems.parentItems = salesOrderModel.items;
      this.filterItemQty0(regroupedItems);
      salesOrderModel.regroupedItems = regroupedItems;
    }
    await this.updateDrawingData(params.name, salesOrderModel);
  }

  async drawing(filename: string): Promise<ProductionDrawing> {
    console.log(`Resolving ${filename}`);
    return firstValueFrom(docData<ProductionDrawing>(doc(getFirestore(), `${PRODUCTION_DRAWINGS_COLLECTION}/${filename?.toUpperCase()}`), ID_FIELD_OPTIONS));
  }

  search(q: Query): Observable<SearchResult<ProductionDrawing>> {
    const preFilters: QueryConstraint[] = [];
    if (q.filtersOps) {
      q.filtersOps.forEach((filter) => preFilters.push(where(filter.field, filter.op, filter.value)));
      delete q.filtersOps;
    }

    if (q.filters) {
      Object.keys(q.filters).forEach((filterKey) => preFilters.push(where(filterKey, '==', q.filters[filterKey])));
      delete q.filters;
    }

    return this.service.observeCollectionSearch(q, ['name', 'path', 'params.drawingParameters.customerName', 'params.drawingParameters.model'], preFilters);
  }

  rawSearch(q: Query): Promise<SearchResult<ProductionDrawing>> {
    return this.queryService.collection<ProductionDrawing>(PRODUCTION_DRAWINGS_COLLECTION, q, [
      'params.drawingParameters.model',
      'params.drawingParameters.customerName',
      'name',
      'path',
    ]);
  }

  async drawingCount(q: Query): Promise<number> {
    const drawings = await firstValueFrom(this.search(q));
    return drawings.result?.length || 0;
  }

  async resolve(route: ActivatedRouteSnapshot): Promise<string> {
    console.log(`Resolving ${route.params['filename']} for item ${route.params['id']}.`);
    const drawing = await this.drawing(route.params['filename']);
    if (drawing) {
      return this.get(drawing.path, ProductionDrawingFileType.SVG);
    }
    return DRAWING_NOT_FOUND;
  }

  async download(path: string, fileType: ProductionDrawingFileType): Promise<string> {
    let drawing = DRAWING_NOT_FOUND;
    try {
      drawing = await this.http.getRaw(`df-drawings/${path.replace(ProductionDrawingFileType.DXF, fileType)}`);
    } catch (err) {
      console.error(`Cannot download drawing. [${path}, ${fileType}, ${err}]`);
    }

    return drawing;
  }

  async get(path: string, fileType: ProductionDrawingFileType): Promise<string> {
    try {
      const drawingRef = this.getDrawingRef(path.replace(ProductionDrawingFileType.DXF, fileType));
      return await getDownloadURL(drawingRef);
    } catch (err) {
      console.log(`Drawing does not exists. [${path}]`);
    }
    return DRAWING_NOT_FOUND;
  }

  async delete(path: string): Promise<void> {
    try {
      const dxfDrawingRef = this.getDrawingRef(path);
      await deleteObject(dxfDrawingRef);
      const svgDrawingRef = this.getDrawingRef(path.replace(ProductionDrawingFileType.DXF, ProductionDrawingFileType.SVG));
      await deleteObject(svgDrawingRef);
    } catch (err) {
      console.log(`Drawing does not exists. [${path}]`);
    }
  }

  async generateDrawing(drawingData: UnitGenerationParameters): Promise<Observable<DocumentData>> {
    this.filterItemQty0(drawingData.regroupedItems);
    const data = { topicPrefix: this.environment.appConfig.production.pubSubPrefix, drawing: drawingData, type: drawingData.drawingType };
    const id = await this.http.post<string>('df-drawings', data);
    return docData(doc(getFirestore(), `${PRODUCTION_DRAWING_STATUS_COLLECTION}/${id}`), ID_FIELD_OPTIONS);
  }

  async generateModelOnlyDrawing(salesOrderModel: SalesOrderModel): Promise<string> {
    const data = { salesOrderModel, drawing: { jobNumber: salesOrderModel.salesOrder?.job }, type: ProductionDrawingProductType.SUPPLIER_MODEL };
    return this.http.post<string>('df-drawings', data);
  }

  async deleteDrawingData(drawingId: string): Promise<void> {
    console.log(`Deleting drawing data for ${drawingId}`);
    return this.http.delete(`df-drawings/${drawingId}`);
  }

  getDrawingStatusByName(name: string): Observable<DrawingGenerationResponse> {
    const statusCollection = collection(getFirestore(), PRODUCTION_DRAWING_STATUS_COLLECTION);
    const queryData = collectionData(query(statusCollection, where('params.filename', '==', name), orderBy('lastModifiedDate', 'desc'), limit(1)), ID_FIELD_OPTIONS);
    return queryData.pipe(map((docSnapshots: DocumentData) => (docSnapshots?.length ? docSnapshots[0] : undefined)));
  }

  async uploadReplace(file: File, drawing: ProductionDrawing): Promise<void> {
    let job: string = drawing.params.drawingParameters?.jobNumber;
    if (!job) {
      const fileName: ProductionDrawingFilename = parseProductionDrawingFileName(drawing.name);
      job = fileName.job;
    }

    await this.gcpUpload(file, drawing.name, job, undefined, drawing);
  }

  async uploadNew(file: File, drawingParameters: GenerationParameters): Promise<void> {
    const job = drawingParameters.jobNumber;
    await this.gcpUpload(file, file.name, job, drawingParameters);
  }

  drawingMaterialsByJob(job: string): Observable<ProductionMaterial[]> {
    return collectionData<ProductionMaterial>(
      query(collection(getFirestore(), PRODUCTION_DRAWING_ITEMS_COLLECTION), where('job', '==', job), where('section', '!=', 'unit')),
      ID_FIELD_OPTIONS,
    );
  }

  drawingMaterialsByFilename(filename: string): Observable<ProductionMaterial[]> {
    return collectionData<ProductionMaterial>(
      query(collection(getFirestore(), PRODUCTION_DRAWING_ITEMS_COLLECTION), where('filename', '==', filename), where('section', '!=', 'unit')),
      ID_FIELD_OPTIONS,
    );
  }

  addDrawingItem(item: ProductionMaterial): Promise<void> {
    item.description = item.description.toLowerCase();
    item.qty = +item.qty;

    return this.dtoSignerService
      .add(item, PRODUCTION_DRAWING_ITEMS_COLLECTION)
      .then((newRef) => console.log(`Added manual entry in production drawing items.`, newRef.id, item.description, item.qty));
  }

  async updateDrawingItem(color: string, item: ProductionMaterial, qty: number, productionItem: ProductionItem): Promise<void> {
    const newQty = qty - item.qty;
    if (newQty) {
      const drawingItem: ProductionMaterial = {
        description: item.description.toLowerCase(),
        productionItemId: +productionItem._id,
        job: productionItem.job,
        qty: newQty,
        colorName: color,
        section: 'bom',
        filename: 'manual',
        suspect: false,
      };

      await this.dtoSignerService.add(drawingItem, PRODUCTION_DRAWING_ITEMS_COLLECTION);
      console.log(`Added manual entry in production drawing items.`, item.description, newQty);
    }
  }

  updateDrawingItems(filename: string, unitType: string, items: ProductionMaterial[]): Observable<DrawingGenerationResponse> {
    return new Observable<DrawingGenerationResponse>((observer: Subscriber<DrawingGenerationResponse>) => {
      const sanitized = this.buildProductionItemsList(items);
      const firestore = getFirestore();

      this.http
        .put<string>(`df-materials/${filename}`, {
          topicPrefix: this.environment.appConfig.production.pubSubPrefix,
          filename,
          unitType,
          items: sanitized,
        })
        .then((id: string) => {
          const progressDocObservable = docData(doc(firestore, `${PRODUCTION_DRAWING_STATUS_COLLECTION}/${id}`), ID_FIELD_OPTIONS);
          observer.add(progressDocObservable.subscribe((response: DocumentData) => observer.next(response as DrawingGenerationResponse)));
        })
        .catch((err) => observer.error(err));
    });
  }

  async listDrawingItemDescriptions(searchPhrase?: string): Promise<string[]> {
    const q: Query = {
      from: 0,
      size: 10,
      searchPhrase,
    };

    const result = await this.queryService.collection<{ _id: null; description: string }>(PRODUCTION_DRAWING_ITEMS_DESCRIPTIONS_COLLECTION, q, ['description']);
    return result.result.map((description) => description.description);
  }

  async listCuttingItemDescriptions(group: string, searchPhrase?: string): Promise<string[]> {
    const q: Query = {
      from: 0,
      size: 100,
      searchPhrase,
      filters: { group },
    };

    const result = await this.queryService.collection<{ _id: null; description: string }>(PRODUCTION_CUTTING_ITEMS_DESCRIPTIONS_COLLECTION, q, ['description']);
    return result.result.map((description) => description.description);
  }

  searchSuppliers(queryObj: Query): Observable<Partial<Supplier>[]> {
    return collectionData(collection(getFirestore(), PRODUCTION_SUPPLIERS_COLLECTION), ID_FIELD_OPTIONS).pipe(
      debounceTime(200),
      map((suppliers) => this.queryService.search(suppliers, queryObj, ['name']).result),
    );
  }

  private buildProductionItemsList(items: ProductionMaterial[]): ProductionMaterial[] {
    const clonedArray = [...items].sort(sortProductionMaterialByIndex);
    const sanitized = clonedArray.filter((pm) => pm.qty > 0);
    const inx: { [section: string]: number } = {};
    sanitized.forEach((pm) => {
      if (!inx[pm.section]) {
        inx[pm.section] = 0;
      }
      pm.index = inx[pm.section];
      inx[pm.section]++;
    });

    return sanitized;
  }

  private getDrawingRef(path: string): StorageReference {
    const storage = getStorage(getApp(), this.environment.appConfig.production.bucket.drawings);
    return ref(storage, path);
  }

  async updateDrawingModelItems(drawing: GenerationParameters): Promise<void> {
    const storage = getStorage(getApp(), this.environment.appConfig.production.bucket.drawings);
    const { jobNumber, filename } = drawing;
    const fullPath = `data/production/${jobNumber}/drawings/${filename}`;
    const drawingRef = ref(storage, fullPath);
    const metadataParams = await fetchFileMetadata(drawingRef);

    if (metadataParams) {
      const updatedMetadata: UploadMetadata = {
        customMetadata: {
          drawingParameters: null,
        },
      };
      const paramsToEncode = {
        ...metadataParams,
        drawingParameters: {
          ...(metadataParams['drawingParameters'] && metadataParams['drawingParameters']),
          modelItems: drawing.modelItems,
        },
      };
      updatedMetadata.customMetadata['drawingParameters'] = unicodeBtoa(JSON.stringify(paramsToEncode));
      await updateMetadata(drawingRef, updatedMetadata);
    }
  }

  private async gcpUpload(file: File, filename: string, job: string, newParams?: GenerationParameters, drawing?: ProductionDrawing): Promise<void> {
    const storage = getStorage(getApp(), this.environment.appConfig.production.bucket.drawings);
    const formattedFileName = filename.toUpperCase().replace(/[^A-Za-z0-9\-._]/g, '');
    const fullPath = `data/production/${job}/drawings/${formattedFileName}`;
    const drawingRef = ref(storage, fullPath);

    let drawingParameters: ProductionDrawingParams = {};
    if (newParams) {
      drawingParameters.new = true;
      drawingParameters.drawingParameters = newParams;
    } else {
      drawingParameters = fillDrawingParameters(await fetchFileMetadata(drawingRef), drawing);
    }
    drawingParameters = await this.getMetadata(formattedFileName, job, drawingParameters);
    const base64Params = unicodeBtoa(JSON.stringify(drawingParameters));

    try {
      const meta: UploadMetadata = {
        contentType: 'application/dxf',
        customMetadata: { drawingParameters: base64Params },
      };
      await uploadBytes(drawingRef, file, meta);
    } catch (e) {
      // Fallback to CF Upload
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      const data: string = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result as string);
      });

      await this.http.put(`df-drawings/${fullPath}`, { base64Params, file: btoa(data) });
    }
  }

  private async getMetadata(filename: string, job: string, params?: ProductionDrawingParams): Promise<ProductionDrawingParams> {
    const currentUser = await this.authService.currentUser();
    const isNew = !!params?.new;
    const updatedParams: ProductionDrawingParams = {
      ...(params || {}),
      processed: false,
      lastModifiedBy: currentUser._id,
      new: isNew,
    };

    if (isNew) {
      updatedParams.filename = filename;
      updatedParams.createdBy = currentUser._id;
      updatedParams.productType = getProductType(filename);
      if (!updatedParams.drawingParameters) {
        updatedParams.drawingParameters = { jobNumber: job };
      }
    } else {
      updatedParams.overwriteCnt = (updatedParams.overwriteCnt || 0) + 1;
    }
    if (updatedParams.drawingParameters) {
      updatedParams.drawingParameters.filename = filename;
    }
    return updatedParams;
  }

  getByJob(job: string): Observable<ProductionDrawing[]> {
    return collectionData<ProductionDrawing>(
      query(collection(getFirestore(), PRODUCTION_DRAWINGS_COLLECTION), where('params.drawingParameters.jobNumber', '==', job)),
      ID_FIELD_OPTIONS,
    );
  }

  private filterItemQty0(regroupedItems: RegroupedItem): void {
    if (regroupedItems) {
      regroupedItems.suggestions = regroupedItems.suggestions?.filter((item) => +item.qty > 0) || [];
      regroupedItems.manual = regroupedItems.manual?.filter((item) => +item.qty > 0) || [];
    }
  }
}

export const drawingResolver: ResolveFn<ProductionDrawing> = (route: ActivatedRouteSnapshot): Promise<ProductionDrawing> => {
  return inject(ProductionDrawingsService).drawing(route.paramMap.get('filename')!);
};
